import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type {
	DetectYogasResponse,
	GetYogaResponse,
	ListYogasResponse,
} from '../types/index.js';
import { RoxyDataElement } from '../utils/base-element.js';
import { baseStyles } from '../utils/base-styles.js';

type YogaListData =
	| ListYogasResponse
	| GetYogaResponse
	| DetectYogasResponse
	| { yogas: Array<GetYogaResponse> };

/**
 * Yoga catalog and detail renderer. Accepts four data modes:
 *   - Catalog: ListYogasResponse (yogas array of {id, name} + total)
 *   - Detail: GetYogaResponse (single yoga with description, result, quality)
 *   - Detail array: { yogas: Array<GetYogaResponse> } for pre-filtered sets
 *   - Detect: DetectYogasResponse (each yoga carries a present verdict + evidence); present yogas render first, each badged present/absent with its classical evidence
 *
 * All multi-item modes include a live search filter.
 */
@customElement('roxy-yoga-list')
export class RoxyYogaList extends RoxyDataElement<YogaListData> {
	static styles = [
		baseStyles,
		css`.wrap{gap:var(--roxy-space-md,1rem);display:grid}.head{justify-content:space-between;align-items:baseline;gap:var(--roxy-space-sm,.5rem);flex-wrap:wrap;display:flex}.title{font-size:var(--roxy-text-lg,1.125rem);font-weight:var(--roxy-weight-bold,600);margin:0}.count{font-size:var(--roxy-text-sm,.875rem);color:var(--roxy-muted,#71717a)}.search-wrap{align-items:center;gap:var(--roxy-space-sm,.5rem);display:flex}.search{width:100%;max-width:280px;font-size:var(--roxy-text-sm,.875rem);font-family:var(--roxy-font-sans);border:1px solid var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-md,8px);background:var(--roxy-surface,#fff);color:var(--roxy-fg,#0a0a0a);outline:none;padding:.35em .75em}.search::placeholder{color:var(--roxy-fg,#0a0a0a);opacity:.65}.search:focus{border-color:var(--roxy-accent,#f59e0b);box-shadow:0 0 0 2px color-mix(in srgb, var(--roxy-accent,#f59e0b) 30%, transparent)}.grid{gap:var(--roxy-space-sm,.5rem);grid-template-columns:repeat(auto-fill,minmax(180px,1fr));display:grid}.yoga-chip{border:1px solid var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-md,8px);font-size:var(--roxy-text-sm,.875rem);background:var(--roxy-surface,#fff);color:var(--roxy-fg,#0a0a0a);word-break:break-word;padding:.4em .8em}.yoga-chip .yoga-id{color:var(--roxy-fg,#0a0a0a);opacity:.75;margin-top:.15em;font-size:.7em;display:block}.detail-card{border:1px solid var(--roxy-border,#e4e4e7);border-radius:var(--roxy-radius-md,8px);padding:var(--roxy-space-md,1rem);background:var(--roxy-surface,#fff);gap:var(--roxy-space-sm,.5rem);display:grid}.detail-name{font-size:var(--roxy-text-lg,1.125rem);font-weight:var(--roxy-weight-bold,600);align-items:center;gap:var(--roxy-space-sm,.5rem);flex-wrap:wrap;margin:0;display:flex}.quality-chip{font-size:var(--roxy-text-xs,.75rem);border-radius:999px;padding:.15em .6em;font-weight:600;display:inline-block}.quality-Positive{background:color-mix(in srgb, var(--roxy-success,#22c55e) 18%, transparent);color:var(--roxy-success-fg,#15803d);border:1px solid color-mix(in srgb, var(--roxy-success,#22c55e) 40%, transparent)}.quality-Negative{background:color-mix(in srgb, var(--roxy-danger,#ef4444) 18%, transparent);color:var(--roxy-danger-fg,#b91c1c);border:1px solid color-mix(in srgb, var(--roxy-danger,#ef4444) 40%, transparent)}.quality-Both{background:color-mix(in srgb, var(--roxy-warning,#f59e0b) 18%, transparent);color:var(--roxy-warning-fg,#b45309);border:1px solid color-mix(in srgb, var(--roxy-warning,#f59e0b) 40%, transparent)}.present-badge{font-size:var(--roxy-text-xs,.75rem);border-radius:999px;padding:.15em .6em;font-weight:600;display:inline-block}.present-badge.is-present{background:color-mix(in srgb, var(--roxy-success,#16a34a) 16%, transparent);color:var(--roxy-success-fg,#166534)}.present-badge.is-absent{background:color-mix(in srgb, var(--roxy-border,#e4e4e7) 55%, transparent);color:var(--roxy-fg,#0a0a0a)}.detail-card.absent{opacity:.72}.evidence{font-size:var(--roxy-text-xs,.75rem);color:var(--roxy-muted,#71717a);line-height:var(--roxy-leading-normal,1.5);margin:0}.description{font-size:var(--roxy-text-sm,.875rem);color:var(--roxy-muted,#71717a);line-height:var(--roxy-leading-normal,1.5);margin:0}details{font-size:var(--roxy-text-sm,.875rem)}details summary{cursor:pointer;color:var(--roxy-accent-ink,#b45309);align-items:center;gap:.4em;padding:.25em 0;font-weight:500;list-style:none;display:flex}details summary:before{content:"+";font-size:1.1em;line-height:1}details[open] summary:before{content:"-"}details .result-body{padding-top:var(--roxy-space-xs,.25rem);color:var(--roxy-fg,#0a0a0a);line-height:var(--roxy-leading-normal,1.5)}.no-results{color:var(--roxy-muted,#71717a);font-size:var(--roxy-text-sm,.875rem);padding:var(--roxy-space-md,1rem) 0;text-align:center}.detail-grid{gap:var(--roxy-space-sm,.5rem);display:grid}`,
	];

	@state()
	private filter = '';

	private readonly handleInput = (e: Event) => {
		this.filter = (e.target as HTMLInputElement).value;
	};

	private renderQualityChip(quality: string) {
		const cls = `quality-chip quality-${quality}`;
		return html`<span class="${cls}">${quality}</span>`;
	}

	private renderDetailCard(yoga: GetYogaResponse) {
		return html`<div class="detail-card"><p class="detail-name">${yoga.name} ${yoga.quality ? this.renderQualityChip(yoga.quality) : nothing}</p>${
				yoga.description
					? html`<p class="description">${yoga.description}</p>`
					: nothing
			} ${
				yoga.result
					? html`<details><summary>Effects</summary><div class="result-body">${yoga.result}</div></details>`
					: nothing
			}</div>`;
	}

	/** Detect-mode card: shows the present/absent verdict and the classical evidence that triggered or failed it. */
	private renderDetectCard(y: DetectYogasResponse['yogas'][number]) {
		return html`<div class="detail-card ${y.present ? '' : 'absent'}"><p class="detail-name">${y.name} ${y.quality ? this.renderQualityChip(y.quality) : nothing} <span class="present-badge ${y.present ? 'is-present' : 'is-absent'}">${y.present ? 'Present' : 'Not present'}</span></p>${y.description ? html`<p class="description">${y.description}</p>` : nothing} ${
				y.present && y.result
					? html`<details><summary>Effects</summary><div class="result-body">${y.result}</div></details>`
					: nothing
			} ${y.evidence ? html`<p class="evidence">${y.evidence}</p>` : nothing}</div>`;
	}

	protected renderEmpty() {
		return html`<div class="roxy-empty" role="status">No yoga data</div>`;
	}

	protected renderData(d: YogaListData) {
		const lc = this.filter.toLowerCase();

		// Detail mode: single GetYogaResponse
		if (
			'description' in d &&
			typeof (d as GetYogaResponse).description === 'string'
		) {
			const yoga = d as GetYogaResponse;
			return html`<div class="wrap">${this.renderDetailCard(yoga)}</div>`;
		}

		// Detail-array mode: { yogas: Array<GetYogaResponse> } where items have description
		if ('yogas' in d && Array.isArray((d as { yogas: unknown[] }).yogas)) {
			const allYogas = (
				d as { yogas: Array<GetYogaResponse | { id: string; name: string }> }
			).yogas;

			// Detect mode: every entry carries a `present` verdict. Render present
			// yogas first, mark each present/absent, and surface the classical
			// evidence. Must precede the detail-array check because detect entries
			// also carry `description`.
			if (allYogas.length > 0 && 'present' in allYogas[0]) {
				const detected = allYogas as DetectYogasResponse['yogas'];
				const filtered = lc
					? detected.filter((y) => y.name.toLowerCase().includes(lc))
					: detected;
				const ordered = [
					...filtered.filter((y) => y.present),
					...filtered.filter((y) => !y.present),
				];
				const presentCount =
					(d as DetectYogasResponse).total ??
					detected.filter((y) => y.present).length;
				return html`<div class="wrap"><div class="head"><h2 class="title">Detected yogas</h2><span class="count">${presentCount} of ${detected.length} present</span></div><div class="search-wrap"><input class="search" type="search" placeholder="Filter yogas..." aria-label="Filter detected yogas by name" .value="${this.filter}" @input="${this.handleInput}"></div><div class="detail-grid" role="region" aria-live="polite" aria-label="Detected yogas">${
							ordered.length > 0
								? ordered.map((y) => this.renderDetectCard(y))
								: html`<p class="no-results">No yogas match your search.</p>`
						}</div></div>`;
			}

			const isDetailArray = allYogas.length > 0 && 'description' in allYogas[0];

			if (isDetailArray) {
				const detailYogas = allYogas as GetYogaResponse[];
				const filtered = lc
					? detailYogas.filter((y) => y.name.toLowerCase().includes(lc))
					: detailYogas;
				const total = (d as ListYogasResponse).total;
				return html`<div class="wrap"><div class="head"><h2 class="title">Yoga catalog</h2>${
							total !== undefined
								? html`<span class="count">${total} total</span>`
								: nothing
						}</div><div class="search-wrap"><input class="search" type="search" placeholder="Filter yogas..." aria-label="Filter yoga list by name" .value="${this.filter}" @input="${this.handleInput}"></div><div class="detail-grid" role="region" aria-live="polite" aria-label="Yoga results">${
							filtered.length > 0
								? filtered.map((y) => this.renderDetailCard(y))
								: html`<p class="no-results">No yogas match your search.</p>`
						}</div></div>`;
			}

			// Catalog mode: ListYogasResponse with {id, name} items
			const catalogYogas = allYogas as Array<{ id: string; name: string }>;
			const filtered = lc
				? catalogYogas.filter((y) => y.name.toLowerCase().includes(lc))
				: catalogYogas;
			const total = (d as ListYogasResponse).total;
			return html`<div class="wrap"><div class="head"><h2 class="title">Yoga catalog</h2>${
						total !== undefined
							? html`<span class="count">${total} total</span>`
							: nothing
					}</div><div class="search-wrap"><input class="search" type="search" placeholder="Filter yogas..." aria-label="Filter yoga list by name" .value="${this.filter}" @input="${this.handleInput}"></div><div class="grid" role="region" aria-live="polite" aria-label="Yoga results">${
						filtered.length > 0
							? filtered.map(
									(y) => html`<div class="yoga-chip">${y.name} <span class="yoga-id">${y.id}</span></div>`,
								)
							: html`<p class="no-results">No yogas match your search.</p>`
					}</div></div>`;
		}

		return html`<div class="roxy-empty" role="status">No yoga data</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'roxy-yoga-list': RoxyYogaList;
	}
}
